import { useState, useEffect, useCallback } from 'react';
import { Goal, Stats, LocationData, Upgrades } from '../types';
import { ALL_GOALS } from '../constants';

interface UseGoalsProps {
  stats: Stats;
  locations: Record<string, LocationData>;
  money: number;
  upgrades: Upgrades;
  staffCount?: number;
  staffRoleCount?: number;
  triggerNotification: (message: string, type: 'success' | 'warning' | 'info' | 'error', duration?: number) => void;
  setMoney: React.Dispatch<React.SetStateAction<number>>;
}

interface GoalProgress {
  id: string;
  progress: number;
  percentage: number;
  isComplete: boolean;
  isClaimed: boolean;
}

export const useGoals = ({
  stats,
  locations,
  money,
  upgrades,
  staffCount = 0,
  staffRoleCount = 0,
  triggerNotification,
  setMoney
}: UseGoalsProps) => {
  // State for tracking completed and claimed goals
  const [completedGoals, setCompletedGoals] = useState<string[]>([]);
  const [claimedGoals, setClaimedGoals] = useState<string[]>([]);
  // Track which goals have already triggered a completion notification to avoid repeats
  const [notifiedGoals, setNotifiedGoals] = useState<string[]>([]);
  
  // Calculate progress for all goals
  const calculateGoalProgress = useCallback((): GoalProgress[] => {
    const extra = { staffCount, staffRoleCount };
    return ALL_GOALS.map(goal => {
      const currentProgress = goal.progress(stats, locations, money, upgrades, extra);
      const percentage = Math.min(100, Math.floor((currentProgress / goal.target) * 100));
      const isComplete = currentProgress >= goal.target;
      const isClaimed = claimedGoals.includes(goal.id);
      
      return {
        id: goal.id,
        progress: currentProgress,
        percentage,
        isComplete,
        isClaimed
      };
    });
  }, [stats, locations, money, upgrades, claimedGoals, staffCount, staffRoleCount]);
  
  // Check for newly completed goals
  const checkGoalProgress = useCallback(() => {
    const goalProgress = calculateGoalProgress();
    
    // Find newly completed goals (complete but not in completedGoals)
    const newlyCompleted = goalProgress
      .filter(goal => goal.isComplete && !completedGoals.includes(goal.id))
      .map(goal => goal.id);
    
    // If there are newly completed goals, update state and notify
    if (newlyCompleted.length > 0) {
      setCompletedGoals(prev => [...prev, ...newlyCompleted]);
      
      // Notify about each newly completed goal ONCE per goal id
      const toNotify = newlyCompleted.filter(id => !notifiedGoals.includes(id));
      if (toNotify.length > 0) {
        toNotify.forEach(goalId => {
          const goal = ALL_GOALS.find(g => g.id === goalId);
          if (goal) {
            triggerNotification(
              `Goal Completed: ${goal.name} - ${goal.description}. Claim your reward!`,
              'success',
              8000
            );
          }
        });
        setNotifiedGoals(prev => [...prev, ...toNotify]);
      }
    }
    
    return goalProgress;
  }, [calculateGoalProgress, completedGoals, triggerNotification, notifiedGoals]);
  
  // Claim a goal's reward
  const claimGoal = useCallback((goalId: string) => {
    // Find the goal
    const goal = ALL_GOALS.find(g => g.id === goalId);
    
    // Check if goal exists, is completed, and not already claimed
    if (!goal) {
      console.error(`Goal with ID ${goalId} not found`);
      return false;
    }
    
    if (!completedGoals.includes(goalId)) {
      console.error(`Goal with ID ${goalId} is not completed yet`);
      return false;
    }
    
    if (claimedGoals.includes(goalId)) {
      console.error(`Goal with ID ${goalId} has already been claimed`);
      return false;
    }
    
    // Add to claimed goals
    setClaimedGoals(prev => [...prev, goalId]);
    
    // Award the reward
    setMoney(prev => prev + goal.reward);
    
    // Notify the user
    triggerNotification(
      `Reward Claimed: $${goal.reward.toLocaleString()} for completing "${goal.name}"!`,
      'success',
      5000
    );
    
    return true;
  }, [completedGoals, claimedGoals, setMoney, triggerNotification]);
  
  // Check goal progress on initial load and when dependencies change
  useEffect(() => {
    checkGoalProgress();
  }, [stats, locations, money, upgrades, checkGoalProgress]);
  
  // Return the hook's API
  return {
    goals: ALL_GOALS,
    goalProgress: calculateGoalProgress(),
    completedGoals,
    claimedGoals,
    checkGoalProgress,
    claimGoal
  };
};
